import React, { useState, useRef } from "react";
import Button from "@amzn/meridian/button";
import { FormattedMessage } from "react-intl";
import { MODAL_ERROR, MODAL_SUCCESS } from "../SettingsConstant";
import { validateTimes } from "../../util/Util";
import { addMilliseconds, parse } from "date-fns";

const OpsClockUpload = ({
  dispatch,
  isUserAuthorisedForSettingsEdit,
  handleClockEventTimeOverride,
  addRemainingProcesses,
  handleUpdateOpsClock,
  timeZone,
  currentDate,
  viewModel,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [hasError] = useState(false);
  const fileInputRef = useRef(null);

  // Error boundary-like behavior for this component
  if (hasError) {
    return (
      <Button disabled={true}>
        <FormattedMessage
          id="opsClock.uploadError"
          defaultMessage="Upload Unavailable"
        />
      </Button>
    );
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateCSVHeaders = (headers) => {
    const requiredHeaders = [
      "process",
      "cycleName",
      "startTime",
      "endTime",
      "defaultStartDate",
    ];

    const normalizedHeaders = headers.map((h) => h.trim().toLowerCase());
    const missingHeaders = requiredHeaders.filter(
      (header) => !normalizedHeaders.includes(header.toLowerCase()),
    );

    if (missingHeaders.length > 0) {
      throw new Error(
        `Missing required CSV headers: ${missingHeaders.join(", ")}`,
      );
    }

    return headers;
  };

  const validateProcessValue = (process) => {
    const validProcesses = ["INDUCT", "STOW", "DISPATCH", "PICK", "TWILIGHT"];
    if (!validProcesses.includes(process.toUpperCase())) {
      throw new Error(
        `Invalid process value: ${process}. Must be one of: ${validProcesses.join(", ")}`,
      );
    }
    return process.toUpperCase();
  };

  const validateTimeFormat = (timeStr, fieldName) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (!timeRegex.test(timeStr)) {
      throw new Error(
        `Invalid time format for ${fieldName}: ${timeStr}. Expected format: HH:MM:SS`,
      );
    }
    return timeStr;
  };

  const validateDateFormat = (dateStr, fieldName) => {
    // Support multiple date formats: MM/DD/YYYY, MM/DD/YY, YYYY-MM-DD
    const formats = [
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, format: "MM/DD/YYYY" },
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, format: "MM/DD/YY" },
      { regex: /^\d{4}-\d{2}-\d{2}$/, format: "YYYY-MM-DD" },
    ];

    let parsedDate = null;

    for (const format of formats) {
      const match = dateStr.match(format.regex);
      if (match) {
        if (format.format === "MM/DD/YYYY") {
          const [, month, day, year] = match;
          parsedDate = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
          );
        } else if (format.format === "MM/DD/YY") {
          const [, month, day, year] = match;
          // Convert 2-digit year to 4-digit (assume 20xx for years 00-99)
          const fullYear =
            parseInt(year, 10) < 50
              ? 2000 + parseInt(year, 10)
              : 1900 + parseInt(year, 10);
          parsedDate = new Date(
            fullYear,
            parseInt(month, 10) - 1,
            parseInt(day, 10),
          );
        } else if (format.format === "YYYY-MM-DD") {
          parsedDate = new Date(dateStr);
        }
        break;
      }
    }

    if (!parsedDate || isNaN(parsedDate.getTime())) {
      throw new Error(
        `Invalid date format for ${fieldName}: ${dateStr}. Expected formats: MM/DD/YYYY, MM/DD/YY, or YYYY-MM-DD`,
      );
    }

    // Return date in YYYY-MM-DD format for API consistency
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const parseCSVContent = (csvContent) => {
    const lines = csvContent.trim().split("\n");
    if (lines.length < 2) {
      throw new Error(
        "CSV file must contain at least a header row and one data row",
      );
    }

    const headers = lines[0].split(",").map((h) => h.trim());
    validateCSVHeaders(headers);

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.length !== headers.length) {
        throw new Error(
          `Row ${i + 1} has ${values.length} columns but expected ${headers.length}`,
        );
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });

      // Validate required fields
      if (
        !row.process ||
        !row.cycleName ||
        !row.startTime ||
        !row.endTime ||
        !row.defaultStartDate
      ) {
        throw new Error(`Row ${i + 1} is missing required fields`);
      }

      // Validate and normalize data
      row.process = validateProcessValue(row.process);
      row.startTime = validateTimeFormat(
        row.startTime,
        `startTime in row ${i + 1}`,
      );
      row.endTime = validateTimeFormat(row.endTime, `endTime in row ${i + 1}`);
      row.defaultStartDate = validateDateFormat(
        row.defaultStartDate,
        `defaultStartDate in row ${i + 1}`,
      );

      if (row.defaultEndDate) {
        row.defaultEndDate = validateDateFormat(
          row.defaultEndDate,
          `defaultEndDate in row ${i + 1}`,
        );
      }

      data.push(row);
    }

    return data;
  };

  const transformCSVToOpsClockFormat = (csvData) => {
    if (csvData.length === 0) {
      throw new Error("No data rows found in CSV");
    }

    // Get dispatch dates from first row
    const firstRow = csvData[0];
    const dispatchDate = firstRow.defaultStartDate;
    const dispatchEndDate = firstRow.defaultEndDate || null;

    // Group data by cycle and process
    const clockEventsMap = new Map();

    csvData.forEach((row, index) => {
      // Validate that all rows have consistent dispatch dates
      if (row.defaultStartDate !== dispatchDate) {
        throw new Error(
          `Row ${index + 2} has different defaultStartDate (${row.defaultStartDate}) than first row (${dispatchDate})`,
        );
      }

      if (
        dispatchEndDate &&
        row.defaultEndDate &&
        row.defaultEndDate !== dispatchEndDate
      ) {
        throw new Error(
          `Row ${index + 2} has different defaultEndDate (${row.defaultEndDate}) than first row (${dispatchEndDate})`,
        );
      }

      const key = `${row.process}_${row.cycleName}`;

      if (clockEventsMap.has(key)) {
        throw new Error(
          `Duplicate entry found for process ${row.process} and cycle ${row.cycleName}`,
        );
      }

      // Don't validate cross-midnight times here - let validateTimes function handle it
      // Convert time to epoch using same logic as handleClockEventTimeOverride
      const startDateTime = `${dispatchDate} ${row.startTime}`;
      const endDateTime = `${dispatchDate} ${row.endTime}`;

      let startDate = new Date(startDateTime);
      let endDate = new Date(endDateTime);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error(`Invalid date/time combination in row ${index + 2}`);
      }

      // Apply timezone conversion and cross-midnight logic exactly like handleClockEventTimeOverride
      let startEpoch, endEpoch;

      if (timeZone) {
        // Calculate timezone offset (same as handleClockEventTimeOverride)
        const timeZoneConvertedStartDate = new Date(
          startDate.toLocaleString("en-US", { timeZone }),
        );
        const timeZoneOffset =
          startDate.getTime() - timeZoneConvertedStartDate.getTime();

        // Calculate reset date (same logic as handleClockEventTimeOverride)
        let resetDate = new Date(dispatchDate);
        if (resetDate && resetDate.getTimezoneOffset() > 0) {
          resetDate.setDate(resetDate.getDate() + 1);
        }

        // Get reset time if available
        const resetTime =
          viewModel.resetTime &&
          viewModel.resetTime.settingsData &&
          viewModel.resetTime.settingsData.shiftTimeRange &&
          viewModel.resetTime.settingsData.shiftTimeRange.endTime;

        const parsedResetTime = resetTime
          ? addMilliseconds(
              parse(
                `${resetTime.hour}:${resetTime.minute}:${resetTime.second}`,
                "HH:mm:ss",
                resetDate,
              ),
              timeZoneOffset,
            )
          : null;

        // Use the same logic as handleClockEventTimeOverride for epoch calculation
        let newStartDateTimeZone = new Date(
          startDate.getTime() + timeZoneOffset,
        );
        let newEndDateTimeZone = new Date(endDate.getTime() + timeZoneOffset);

        let startEpochBeforeValidation = newStartDateTimeZone.getTime() / 1000;
        let endEpochBeforeValidation = newEndDateTimeZone.getTime() / 1000;

        // Apply cross-midnight validation using validateTimes - this may adjust the dates
        let validatedStartTime, validatedEndTime;
        try {
          validatedStartTime = validateTimes(
            new Date(startEpochBeforeValidation * 1000),
            dispatchDate,
            viewModel.opsClockData,
            true,
            row.process,
            parsedResetTime,
          );

          validatedEndTime = validateTimes(
            new Date(endEpochBeforeValidation * 1000),
            dispatchDate,
            viewModel.opsClockData,
            true,
            row.process,
            parsedResetTime,
          );
        } catch (validateError) {
          // If validateTimes fails (likely due to missing clockEvents), use the original times
          console.warn(
            `validateTimes failed for ${row.process}, using original times:`,
            validateError.message,
          );
          validatedStartTime = new Date(startEpochBeforeValidation * 1000);
          validatedEndTime = new Date(endEpochBeforeValidation * 1000);
        }

        // Convert back to epoch seconds (not milliseconds)
        startEpoch = Math.floor(validatedStartTime.getTime() / 1000);
        endEpoch = Math.floor(validatedEndTime.getTime() / 1000);

        // Debug: Check if we have invalid time sequence after validation
        if (endEpoch <= startEpoch) {
          console.warn(
            `Warning: Invalid time sequence after validateTimes for ${row.process} in ${row.cycleName}: start=${startEpoch}, end=${endEpoch}`,
          );
          // For cross-midnight cases, add 24 hours to the end time
          endEpoch = endEpoch + 24 * 60 * 60; // Add 24 hours in seconds
          console.log(
            `Fixed cross-midnight time for ${row.process}: new endEpoch=${endEpoch}`,
          );
        }
      } else {
        // Fallback to simple conversion if no timezone available
        startEpoch = Math.floor(startDate.getTime() / 1000);
        endEpoch = Math.floor(endDate.getTime() / 1000);
      }

      clockEventsMap.set(key, {
        process: row.process,
        cycleName: row.cycleName,
        timeIntervals: [
          {
            startTime: startEpoch,
            endTime: endEpoch,
          },
        ],
      });
    });

    // Let the existing validateTimes function and UI validation handle all time sequence validation
    // This ensures consistency with the override functionality

    const clockEvents = Array.from(clockEventsMap.values());

    const opsClockData = {
      nodeId:
        (viewModel.opsClockData.opsClock &&
          viewModel.opsClockData.opsClock.nodeId) ||
        null,
      nodeType:
        (viewModel.opsClockData.opsClock &&
          viewModel.opsClockData.opsClock.nodeType) ||
        null,
      isDefault: true,
      dispatchDate: dispatchDate,
      dispatchEndDate: dispatchEndDate,
      overrideClockEvents: [],
      clockEvents: clockEvents,
      breaks: [],
    };

    return opsClockData;
  };

  const processCSVUpload = async (csvData) => {
    try {
      // Validate that we have the necessary functions and data
      if (!viewModel || !viewModel.opsClockData) {
        throw new Error("Required viewModel data is not available");
      }

      // Transform CSV data to ops clock format
      const csvOpsClockData = transformCSVToOpsClockFormat(csvData);

      // Get existing ops clock data or create base structure
      let existingOpsClockData;
      if (
        viewModel.opsClockData.opsClock &&
        viewModel.opsClockData.opsClock.clockEvents
      ) {
        // Use existing data as base
        existingOpsClockData = JSON.parse(
          JSON.stringify(viewModel.opsClockData.opsClock),
        );
      } else {
        // Create base structure if no existing data
        existingOpsClockData = {
          nodeId: csvOpsClockData.nodeId,
          nodeType: csvOpsClockData.nodeType,
          isDefault: true,
          dispatchDate: csvOpsClockData.dispatchDate,
          dispatchEndDate: csvOpsClockData.dispatchEndDate,
          overrideClockEvents: [],
          clockEvents: [],
          breaks: null,
        };
      }

      // Update dispatch dates from CSV
      existingOpsClockData.dispatchDate = csvOpsClockData.dispatchDate;
      existingOpsClockData.dispatchEndDate = csvOpsClockData.dispatchEndDate;

      // Merge CSV data with existing data (CSV data overrides existing)
      const csvClockEventsMap = new Map();
      if (
        csvOpsClockData.clockEvents &&
        Array.isArray(csvOpsClockData.clockEvents)
      ) {
        csvOpsClockData.clockEvents.forEach((csvEvent) => {
          const key = `${csvEvent.process}_${csvEvent.cycleName}`;
          csvClockEventsMap.set(key, csvEvent);
        });
      }

      // Update existing clock events with CSV data or keep existing
      const mergedClockEvents = [];

      // First, add/update with CSV data
      if (
        csvOpsClockData.clockEvents &&
        Array.isArray(csvOpsClockData.clockEvents)
      ) {
        csvOpsClockData.clockEvents.forEach((csvEvent) => {
          mergedClockEvents.push(csvEvent);
        });
      }

      // Then, add existing clock events that weren't in CSV
      if (
        existingOpsClockData.clockEvents &&
        Array.isArray(existingOpsClockData.clockEvents)
      ) {
        existingOpsClockData.clockEvents.forEach((existingEvent) => {
          const key = `${existingEvent.process}_${existingEvent.cycleName}`;
          if (!csvClockEventsMap.has(key)) {
            // Keep existing event if not overridden by CSV
            mergedClockEvents.push(existingEvent);
          }
        });
      }

      // Create final ops clock data
      const finalOpsClockData = {
        ...existingOpsClockData,
        isDefault: true, // Always set to true for upload requests
        clockEvents: mergedClockEvents,
        breaks: null, // Must be null, not empty array, to match existing Save functionality
      };

      // Debug: Log the data being sent to the API
      console.log(
        "CSV Upload - Final data being sent to API:",
        JSON.stringify(finalOpsClockData, null, 2),
      );
      console.log(
        "CSV Upload - Clock events count:",
        finalOpsClockData.clockEvents
          ? finalOpsClockData.clockEvents.length
          : 0,
      );
      console.log(
        "CSV Upload - Clock events processes:",
        finalOpsClockData.clockEvents &&
          Array.isArray(finalOpsClockData.clockEvents)
          ? finalOpsClockData.clockEvents.map(
              (ce) => `${ce.process}_${ce.cycleName}`,
            )
          : [],
      );

      // Update the ops clock using existing API
      dispatch({
        type: "UPDATE_OPS_CLOCK_REQUEST",
        data: { dispatcher: dispatch, planData: finalOpsClockData },
      });

      // Only reload if we have a valid currentDate
      if (currentDate) {
        dispatch({
          type: "LOAD_OPS_CLOCK_DATA",
          data: { dispatcher: dispatch, dispatchDate: currentDate },
        });
      }

      dispatch({
        type: "UPDATE_PLAN_RESPONSE",
        data: {
          showUpdatePlanResponseModal: MODAL_SUCCESS,
          message: `Successfully uploaded ${csvData.length} ops clock entries`,
        },
      });
    } catch (error) {
      dispatch({
        type: "UPDATE_PLAN_RESPONSE",
        data: {
          showUpdatePlanResponseModal: MODAL_ERROR,
          message: `CSV Upload Error: ${error.message}`,
        },
      });
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith(".csv")) {
      dispatch({
        type: "UPDATE_PLAN_RESPONSE",
        data: {
          showUpdatePlanResponseModal: MODAL_ERROR,
          message: "Please select a CSV file",
        },
      });
      return;
    }

    // Validate file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      dispatch({
        type: "UPDATE_PLAN_RESPONSE",
        data: {
          showUpdatePlanResponseModal: MODAL_ERROR,
          message: "File size must be less than 5MB",
        },
      });
      return;
    }

    setIsUploading(true);

    try {
      const csvContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error("Failed to read file"));
        reader.readAsText(file);
      });

      const csvData = parseCSVContent(csvContent);
      await processCSVUpload(csvData);
    } catch (error) {
      dispatch({
        type: "UPDATE_PLAN_RESPONSE",
        data: {
          showUpdatePlanResponseModal: MODAL_ERROR,
          message: `CSV Processing Error: ${error.message}`,
        },
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: "none" }}
      />
      <Button
        className="opsClockFilterButton"
        disabled={!isUserAuthorisedForSettingsEdit || isUploading}
        onClick={handleUploadClick}
      >
        {isUploading ? (
          <FormattedMessage
            id="opsClock.uploading"
            defaultMessage="Uploading..."
          />
        ) : (
          <FormattedMessage id="opsClock.upload" defaultMessage="Upload" />
        )}
      </Button>
    </div>
  );
};

export default OpsClockUpload;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/OpsClockUpload.js